import { useState } from 'react';
import { CreditCard, CheckCircle2, AlertCircle, ExternalLink, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { useStartStripeConnect } from './useStartStripeConnect';
import { useRefreshStripeConnectStatus } from './useRefreshStripeConnectStatus';
import { useCreateStripeDashboardLink } from './useCreateStripeDashboardLink';

interface StripeAccount {
  id: string;
  status: string;
  stripeAccountId?: string | null;
  connected: boolean;
  active: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  onboardingComplete: boolean;
}

interface PaymentsTabProps {
  schoolId: string | undefined;
  stripeAccount?: StripeAccount | null;
  onRefetch?: () => void;
}

export function PaymentsTab({ schoolId, stripeAccount, onRefetch }: PaymentsTabProps) {
  const [isConnecting, setIsConnecting] = useState(false);

  const [startStripeConnect] = useStartStripeConnect();
  const [refreshStatus, { loading: isRefreshing }] = useRefreshStripeConnectStatus();
  const [createDashboardLink, { loading: isOpeningDashboard }] = useCreateStripeDashboardLink();

  const handleConnectStripe = async () => {
    setIsConnecting(true);
    try {
      const { data } = await startStripeConnect({
        variables: {
          input: { schoolId: schoolId! },
        },
      });
      if (data?.startStripeConnect?.onboardingUrl) {
        window.location.href = data.startStripeConnect.onboardingUrl;
      }
    } finally {
      setIsConnecting(false);
    }
  };

  const handleRefreshStatus = async () => {
    await refreshStatus({
      variables: {
        input: { schoolId: schoolId! },
      },
    });
    onRefetch?.();
  };

  const handleOpenDashboard = async () => {
    const { data } = await createDashboardLink({
      variables: {
        input: { schoolId: schoolId! },
      },
    });
    if (data?.createStripeDashboardLink?.dashboardUrl) {
      window.open(data.createStripeDashboardLink.dashboardUrl, '_blank');
    }
  };

  const isConnected = stripeAccount?.connected;
  const isActive = stripeAccount?.active;

  return (
    <>
      <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
        <div className="flex items-center gap-3">
          {isActive ? (
            <CheckCircle2 className="h-6 w-6 text-muted-foreground" />
          ) : isConnected ? (
            <AlertCircle className="h-6 w-6 text-[--settings-attention]" />
          ) : (
            <CreditCard className="h-6 w-6 text-[--settings-attention]" />
          )}
          <div>
            <p className="font-medium">
              {isActive
                ? 'Connected to Stripe'
                : isConnected
                  ? 'Connected, with something outstanding'
                  : 'Not connected to Stripe'}
            </p>
            <p className="text-sm text-muted-foreground">
              {isActive
                ? 'Families can pay online, and the money reaches the school.'
                : isConnected
                  ? 'Stripe still needs something from you before money can move.'
                  : 'Families cannot pay online until an account is connected.'}
            </p>
          </div>
        </div>
      </div>

      {isConnected && (
        <dl className="mt-4 text-sm">
          <StatusLine label="Taking payments" ok={!!stripeAccount?.chargesEnabled} />
          <StatusLine label="Paying out to the school" ok={!!stripeAccount?.payoutsEnabled} />
          <StatusLine label="Stripe onboarding finished" ok={!!stripeAccount?.onboardingComplete} />
          {stripeAccount?.stripeAccountId && (
            <div className="flex items-center justify-between border-t py-2.5">
              <dt className="text-muted-foreground">Stripe account</dt>
              <dd>
                <code className="rounded bg-muted px-2 py-0.5 text-xs">
                  {stripeAccount.stripeAccountId}
                </code>
              </dd>
            </div>
          )}
        </dl>
      )}

      {!isConnected && (
        <p className="mt-4 max-w-[62ch] text-sm text-muted-foreground">
          Connecting an account authorizes SolaStack to charge families on the school&rsquo;s
          behalf. The school keeps its own Stripe account and its own dashboard.
        </p>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        {!isConnected && (
          <Button onClick={handleConnectStripe} disabled={isConnecting} className="gap-2">
            {isConnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            {isConnecting ? 'Connecting…' : 'Connect Stripe account'}
          </Button>
        )}

        {isConnected && (
          <>
            <Button variant="outline" onClick={handleOpenDashboard} disabled={isOpeningDashboard} className="gap-2">
              {isOpeningDashboard ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              {isOpeningDashboard ? 'Opening…' : 'Open Stripe dashboard'}
            </Button>
            <Button variant="ghost" onClick={handleRefreshStatus} disabled={isRefreshing} className="gap-2">
              {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {isRefreshing ? 'Checking…' : 'Check again'}
            </Button>
          </>
        )}
      </div>
    </>
  );
}

/**
 * One requirement Stripe either has or has not met. Only the unmet ones are
 * marked, so a fully working account reads as a quiet list rather than a wall
 * of ticks.
 */
function StatusLine({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center justify-between border-t py-2.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={ok ? '' : 'font-medium text-[--settings-attention]'}>
        {ok ? 'Yes' : 'Not yet'}
      </dd>
    </div>
  );
}
